<template>
  <div class="page">

    <div class="page-header">
      <h2 class="page-title">Template SMS</h2>

      <Button
        label="Aggiungi template"
        icon="pi pi-plus"
        @click="openCreateDialog"
      />
    </div>

    <div class="table-wrap">
      <DataTable
        :value="templates"
        :loading="loading"
        dataKey="id"
        paginator
        :rows="10"
        :rowsPerPageOptions="[10, 25, 50]"
        stripedRows
        responsiveLayout="scroll"
      >
        <template #empty>
          Nessun template trovato.
        </template>

        <Column field="name" header="Nome" sortable />
        <Column field="category" header="Categoria" sortable class="col-small" />
        <Column field="language" header="Lingua" sortable class="col-small" />

        <Column field="body" header="Testo" class="col-body">
          <template #body="{ data }">
            <div class="body-preview">{{ data.body }}</div>
          </template>
        </Column>

        <Column field="is_system" header="Sistema" sortable class="col-small">
          <template #body="{ data }">
            <Tag :value="data.is_system ? 'SI' : 'NO'" />
          </template>
        </Column>

        <Column header="Azioni" class="col-actions">
            <template #body="{ data }">
                <div class="row-actions">
                <Button
                    label="Modifica"
                    icon="pi pi-pencil"
                    class="p-button-warning"
                    size="small"
                    :disabled="data.is_system"
                    @click="openEditDialog(data)"
                />

                <!-- <Button
                    label="Disattiva"
                    icon="pi pi-ban"
                    class="p-button-warning"
                    size="small"
                    :disabled="data.is_system"
                    @click="confirmDeactivate(data)"
                /> -->

                <Button
                    label="Elimina"
                    icon="pi pi-trash"
                    class="p-button-danger"
                    size="small"
                    :disabled="data.is_system"
                    @click="confirmDeactivate(data)"
                />
                </div>
            </template>
        </Column>
      </DataTable>
    </div>

    <Dialog
      v-model:visible="dialogVisible"
      modal
      :header="dialogMode === 'create' ? 'Aggiungi template' : 'Modifica template'"
      :style="{ width: '720px' }"
      :closable="true"
    >
      <div class="dialog-form">
        <div class="form-field">
          <label class="form-label">Nome</label>
          <InputText v-model="form.name" maxlength="100" />
          <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
        </div>

        <div class="form-field">
          <label class="form-label">Testo</label>
          <Textarea v-model="form.body" rows="6" autoResize maxlength="1000" />
          <small v-if="errors.body" class="p-error">{{ errors.body }}</small>
        </div>

        <div class="form-grid">
          <div class="form-field">
            <label class="form-label">Categoria (opzionale)</label>
            <InputText v-model="form.category" maxlength="50" />
          </div>

          <div class="form-field">
            <label class="form-label">Lingua (opzionale)</label>
            <InputText v-model="form.language" placeholder="it" maxlength="5" />
          </div>
        </div>
      </div>

      <template #footer>
        <Button label="Annulla" severity="secondary" @click="closeDialog" />
        <Button
          :label="dialogMode === 'create' ? 'Crea' : 'Salva'"
          icon="pi pi-check"
          :loading="saving"
          @click="submitForm"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Tag from "primevue/tag";

import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";

import SmsTemplateService from "@/services/sms-templates/sms-service";

export default {
  name: "SmsTemplates",
  components: {
    Button,
    DataTable,
    Column,
    Dialog,
    InputText,
    Textarea,
    Tag,
  },
  data() {
    return {
      service: null,

      templates: [],
      loading: false,
      saving: false,

      dialogVisible: false,
      dialogMode: "create", // create | edit
      editingId: null,

      form: {
        name: "",
        body: "",
        category: "",
        language: ""
      },

      errors: {
        name: null,
        body: null
      },

      toast: null,
      confirm: null
    };
  },
  created() {
    this.toast = useToast();
    this.confirm = useConfirm();

    this.service = new SmsTemplateService(null);
  },
  mounted() {
    this.fetchTemplates();
  },
  methods: {
    resetErrors() {
      this.errors.name = null;
      this.errors.body = null;
    },

    resetForm() {
      this.form.name = "";
      this.form.body = "";
      this.form.category = "";
      this.form.language = "";
      this.editingId = null;
      this.resetErrors();
    },

    openCreateDialog() {
      this.dialogMode = "create";
      this.resetForm();
      this.dialogVisible = true;
    },

    openEditDialog(t) {
      this.dialogMode = "edit";
      this.resetErrors();
      this.editingId = t.id;

      this.form.name = t.name || "";
      this.form.body = t.body || "";
      this.form.category = t.category ?? "";
      this.form.language = t.language ?? "";

      this.dialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
    },

    validateForm() {
      this.resetErrors();

      const name = (this.form.name || "").trim();
      const body = (this.form.body || "").trim();

      if (!name) this.errors.name = "Il campo name è richiesto";
      if (!body) this.errors.body = "Il campo body è richiesto";

      return !this.errors.name && !this.errors.body;
    },

    async fetchTemplates() {
      this.loading = true;

      try {
        const res = await this.service.getTemplates();

        this.templates = Array.isArray(res?.data) ? res.data : [];
      } catch (e) {
        this.toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Impossibile caricare i template",
          life: 3500
        });
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      if (!this.validateForm()) return;

      this.saving = true;

      try {
        const payload = {
          name: this.form.name.trim(),
          body: this.form.body.trim(),
          category:
            this.form.category && this.form.category.trim() !== ""
              ? this.form.category.trim()
              : null,
          language:
            this.form.language && this.form.language.trim() !== ""
              ? this.form.language.trim()
              : null
        };

        if (this.dialogMode === "create") {
          await this.service.createTemplate(payload);

          this.toast.add({
            severity: "success",
            summary: "Ok",
            detail: "Template creato",
            life: 2500
          });
        } else {
          await this.service.updateTemplate(this.editingId, payload);

          this.toast.add({
            severity: "success",
            summary: "Ok",
            detail: "Template aggiornato",
            life: 2500
          });
        }

        this.dialogVisible = false;
        await this.fetchTemplates();
      } catch (e) {
        const backendMsg = e?.response?.data?.message;

        this.toast.add({
          severity: "error",
          summary: "Errore",
          detail: backendMsg || "Operazione non riuscita",
          life: 3500
        });
      } finally {
        this.saving = false;
      }
    },

    confirmDeactivate(t) {
      this.confirm.require({
        message: `Vuoi disattivare "${t.name}"?`,
        header: "Conferma",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Disattiva",
        rejectLabel: "Annulla",
        accept: () => this.deactivateTemplate(t)
      });
    },

    async deactivateTemplate(t) {
      try {
        await this.service.deactivateTemplate(t.id);

        this.toast.add({
          severity: "success",
          summary: "Ok",
          detail: "Template disattivato",
          life: 2500
        });

        await this.fetchTemplates();
      } catch (e) {
        this.toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Impossibile disattivare il template",
          life: 3500
        });
      }
    },

    confirmDelete(t) {
      this.confirm.require({
        message: `Vuoi eliminare definitivamente "${t.name}"?`,
        header: "Conferma",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Elimina",
        rejectLabel: "Annulla",
        accept: () => this.deleteTemplate(t)
      });
    },

    async deleteTemplate(t) {
      try {
        await this.service.deleteTemplate(t.id);

        this.toast.add({
          severity: "success",
          summary: "Ok",
          detail: "Template eliminato",
          life: 2500
        });

        await this.fetchTemplates();
      } catch (e) {
        this.toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Impossibile eliminare il template",
          life: 3500
        });
      }
    }
  }
};
</script>

<style scoped>
.page {
  padding: 16px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.table-wrap {
  width: 100%;
}

.row-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.body-preview {
  max-width: 520px;
  white-space: normal;
  word-break: break-word;
  line-height: 1.35;
}

/* PrimeVue deep selectors */
:deep(.p-button) {
  white-space: nowrap;
}

:deep(.p-datatable) {
  border-radius: 10px;
  overflow: hidden;
}

:deep(.p-datatable .p-datatable-tbody > tr > td),
:deep(.p-datatable .p-datatable-thead > tr > th) {
  padding: 12px 10px;
  vertical-align: top;
}

:deep(.col-body) {
  width: 520px;
}

:deep(.col-small) {
  width: 140px;
}

:deep(.col-actions) {
  width: 260px;
}

/* Dialog */
.dialog-form {
  display: grid;
  gap: 12px;
}

.form-field {
  display: grid;
  gap: 6px;
}

.form-label {
  font-weight: 600;
}

.form-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

@media (min-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* Header tabella: più contrasto */
:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #4b93bf !important;
  color: #ffffff !important;
  font-weight: 600;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Icone sort + filter nell'header */
:deep(.p-datatable .p-sortable-column .p-sortable-column-icon),
:deep(.p-datatable .p-column-filter-menu-button),
:deep(.p-datatable .p-column-filter-menu-button > span) {
  color: #ffffff !important;
}

/* Hover header (quando sortable) */
:deep(.p-datatable .p-sortable-column:not(.p-highlight):hover) {
  background: #068de0 !important;
  color: #ffffff !important;
}

/* Se una colonna è “attiva” (ordinata) */
:deep(.p-datatable .p-sortable-column.p-highlight) {
  background: #0178c2 !important;
  color: #ffffff !important;
}
</style>
